// src/js/mc/roster.ts
// Phase 253 (253-05, D-07) — click-to-sort binding for the `/mc/<key>` Player Roster table's
// seven headers (Phase 273 D-14/plan 273-10 added the Active Playtime column). Thin DOM assembly
// on top of the zero-import `utils/rosterSort` module, mirroring how `chatlog.ts` is thin DOM
// assembly on top of `utils/chatThread.ts`.
//
// Re-sort mechanics (UI-SPEC §4): a click/Enter/Space toggles the sort state via
// `nextSortState()`, then `applySort()` re-appends the EXISTING `<tr>` nodes into their new order
// with `tbody.appendChild()` — `appendChild` MOVES a node already in the document, so re-sorting
// itself never rebuilds a cell or reformats a value.
//
// Quick-task follow-up to Phase 253 — `updateRosterRows()` below is the ONE place cells DO get
// rebuilt: on each 30s detail-poll tick (mc.ts's `pollDetail()`), the whole tbody is torn down and
// rebuilt from the fresh payload, then `applySort()` re-runs against the table's CURRENT sort
// state (never the server's default) so a live update can never silently reset what the operator
// clicked. The `controllers` WeakMap is what makes `currentState`/`applySort` reachable from
// outside this module's own click/keydown closures without breaking their encapsulation.

import {
    nextSortState,
    parseSortValue,
    sortIndices,
    ariaSortValue,
    sortGlyph,
    DEFAULT_SORT_KEY,
    DEFAULT_SORT_DIRECTION,
    type RosterSortState,
    type SortKind,
} from '../utils/rosterSort';
import { buildMcAvatarTile, buildMcNameWrap, type McIdentityRow } from '../utils/mcIdentity';
import { fillMcValueCell } from '../utils/mcValueFormat';

export interface RosterRow extends McIdentityRow {
    active_playtime: number | null;
    active_playtime_reason: string | null;
    playtime: number | null;
    blocks_mined: number | null;
    ground: number | null;
    water: number | null;
    air_vehicle: number | null;
}

interface RosterController {
    applySort: (state: RosterSortState) => void;
    getState: () => RosterSortState;
    tbody: HTMLTableSectionElement;
}

// Replaces the old idempotency-only WeakSet: presence in this map now means "initialised", AND
// gives updateRosterRows() a way back into applySort()/currentState without a second lookup path.
const controllers = new WeakMap<HTMLTableElement, RosterController>();

/** `data-sort-kind` values are only ever `'text'` or `'numeric'` today (detail.latte's own seven
 *  headers), but a missing/unknown attribute must still degrade to a safe, defined behaviour
 *  rather than `undefined` propagating into `parseSortValue()`. `'numeric'` is that default —
 *  six of the seven columns are numeric, so it is also the majority case. */
function resolveKind(header: HTMLElement): SortKind {
    return header.getAttribute('data-sort-kind') === 'text' ? 'text' : 'numeric';
}

export function initRoster(container: HTMLElement): void {
    const table = container.querySelector<HTMLTableElement>('.mc-roster-table');
    if (!table) return; // Section not present on this page render (an empty roster renders UI-SPEC §4's locked empty-state note instead of the table shell entirely).

    if (controllers.has(table)) return;

    const tbody = table.tBodies[0];
    if (!tbody) return;

    const headers = Array.from(table.querySelectorAll<HTMLElement>('th[data-sort-key]'));
    if (headers.length === 0) return;

    // INITIAL STATE comes FROM THE DOM, not from an assumption. `detail.latte` hard-codes
    // aria-sort="descending" + .mc-roster-th--active on the Active Playtime header (Phase 273
    // D-14/plan 273-10 — it used to be Time Played) to match McAnalyticsModel::getPlayerRoster()'s
    // own default sort — adopting whichever header is actually marked keeps this file in lockstep
    // with the server even if that default ever changes without this file being touched in the
    // same commit. The DEFAULT_SORT_KEY/DEFAULT_SORT_DIRECTION fallback only applies when no
    // header is marked at all.
    let currentState: RosterSortState = { key: DEFAULT_SORT_KEY, direction: DEFAULT_SORT_DIRECTION };
    for (const header of headers) {
        const ariaSort = header.getAttribute('aria-sort');
        if (ariaSort === 'ascending' || ariaSort === 'descending') {
            const key = header.getAttribute('data-sort-key');
            if (key) {
                currentState = { key, direction: ariaSort === 'ascending' ? 'asc' : 'desc' };
            }
            break;
        }
    }

    // Do NOT re-sort here — the server already emitted the rows in currentState's order, and a
    // redundant initial sort is only a chance to silently disagree with it.

    function applySort(state: RosterSortState): void {
        const activeHeader = headers.find((h) => h.getAttribute('data-sort-key') === state.key);
        const kind: SortKind = activeHeader ? resolveKind(activeHeader) : 'numeric';

        const rows = Array.from(tbody!.rows);
        const values = rows.map((tr) => parseSortValue(tr.getAttribute('data-sort-' + state.key), kind));
        const order = sortIndices(values, state.direction);
        for (const i of order) {
            tbody!.appendChild(rows[i]);
        }

        // HEADER SYNC — ALL headers, on EVERY apply, so a previously-active column can never keep
        // a stale glyph, a stale aria-sort or the active class (T-253-26).
        for (const header of headers) {
            const isActive = header.getAttribute('data-sort-key') === state.key;
            header.setAttribute('aria-sort', ariaSortValue(isActive, state.direction));
            const icon = header.querySelector<HTMLElement>('.mc-sort-icon');
            if (icon) {
                icon.textContent = sortGlyph(isActive, state.direction);
            }
            header.classList.toggle('mc-roster-th--active', isActive);
        }

        currentState = state;
    }

    for (const header of headers) {
        const key = header.getAttribute('data-sort-key');
        if (!key) continue;
        const kind = resolveKind(header);

        header.addEventListener('click', () => {
            applySort(nextSortState(currentState, key, kind));
        });

        // The template already supplies role="button" and tabindex="0" on every header, so the
        // keyboard focus path exists — this listener is what makes it actually do something.
        // preventDefault() on Space stops the page from scrolling, matching every other
        // role="button" custom control on this site.
        header.addEventListener('keydown', (event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            if (event.key === ' ') {
                event.preventDefault();
            }
            applySort(nextSortState(currentState, key, kind));
        });
    }

    controllers.set(table, { applySort, getState: () => currentState, tbody });
}

/** The SEVEN roster columns, in DOM order (Phase 273 D-14/plan 273-10 added Active Playtime,
 *  which is why this is seven, not six) — the single source `buildRosterTableShell()` and every
 *  reader of `data-sort-key` (this file, `rosterSort.ts`'s consumers) agree on. Mirrors
 *  detail.latte:622-628 exactly: label, sort key, sort kind, and whether this column starts active
 *  (Active Playtime only, matching `getPlayerRoster()`'s own default sort — see
 *  `McAnalyticsModel::ROSTER_DEFAULT_SORT_KEY` / `sortRosterRowsByActivePlaytimeDescNullsLast()`).
 *  Time Played is no longer the active column; it reverted to ordinary sortable-header styling. */
// NOTE: only the Active Playtime `<th>` carries `data-metric` here -- Time Played's `<th>`
// deliberately does NOT (matching detail.latte:624 exactly, proven at runtime by
// tools/probe-273-10-js-parity.mjs's structural comparison; UI-SPEC's "distinct, greppable
// selector" contract only requires it on the cumulative Active `<th>`/`<td>` PAIR, not on Time
// Played). Time Played's own `<td>` still carries `data-metric="playtime"` -- see
// buildRosterRow() below -- for the cell-level selector symmetry Task 2's own action text asked
// for; only the HEADER is scoped to Active alone.
const ROSTER_COLUMNS: ReadonlyArray<{ label: string; key: string; kind: SortKind; active: boolean; metric?: string }> = [
    { label: 'Player', key: 'player', kind: 'text', active: false },
    { label: 'Active Playtime', key: 'active-playtime', kind: 'numeric', active: true, metric: 'active_playtime' },
    { label: 'Time Played', key: 'playtime', kind: 'numeric', active: false },
    { label: 'Blocks Mined', key: 'blocks-mined', kind: 'numeric', active: false },
    { label: 'Ground', key: 'ground', kind: 'numeric', active: false },
    { label: 'Water', key: 'water', kind: 'numeric', active: false },
    { label: 'Air & Vehicle', key: 'air-vehicle', kind: 'numeric', active: false },
];

/** Builds the empty table shell (thead + empty tbody, no rows) for the cold-start case: a roster
 *  that had zero players at page-render time (server-rendered empty-state paragraph, no table at
 *  all) now has its first player show up on a live poll tick. `createElement` throughout — every
 *  string here is a locked column label, never wire data. */
function buildRosterTableShell(): HTMLTableElement {
    const table = document.createElement('table');
    table.className = 'table table-sm table-striped mc-board-table mc-roster-table';

    const thead = document.createElement('thead');
    const headRow = document.createElement('tr');
    for (const col of ROSTER_COLUMNS) {
        const th = document.createElement('th');
        th.scope = 'col';
        th.setAttribute('role', 'button');
        th.tabIndex = 0;
        th.setAttribute('aria-sort', col.active ? 'descending' : 'none');
        th.setAttribute('aria-label', `Sort by ${col.label}`);
        th.setAttribute('data-sort-key', col.key);
        th.setAttribute('data-sort-kind', col.kind);
        if (col.metric) {
            th.setAttribute('data-metric', col.metric);
        }
        if (col.active) {
            th.classList.add('mc-roster-th--active');
        }
        th.append(`${col.label} `);
        const icon = document.createElement('span');
        icon.className = 'mc-sort-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = col.active ? sortGlyph(true, DEFAULT_SORT_DIRECTION) : sortGlyph(false, 'desc');
        th.appendChild(icon);
        headRow.appendChild(th);
    }
    thead.appendChild(headRow);
    table.appendChild(thead);
    table.appendChild(document.createElement('tbody'));

    return table;
}

/** One `<tr>`, matching detail.latte:633-671's roster row markup exactly (data-sort-* raw values,
 *  the mc-board-player identity cell, six mcValueCell-formatted value cells — Active Playtime
 *  first, threading its absence reason through the fifth argument, then the original five). */
function buildRosterRow(row: RosterRow, serverKey: string): HTMLTableRowElement {
    const tr = document.createElement('tr');
    tr.setAttribute('data-uuid', row.uuid);
    tr.setAttribute('data-sort-player', row.display_name);
    tr.setAttribute('data-sort-active-playtime', row.active_playtime === null ? '' : String(row.active_playtime));
    tr.setAttribute('data-sort-playtime', row.playtime === null ? '' : String(row.playtime));
    tr.setAttribute('data-sort-blocks-mined', row.blocks_mined === null ? '' : String(row.blocks_mined));
    tr.setAttribute('data-sort-ground', row.ground === null ? '' : String(row.ground));
    tr.setAttribute('data-sort-water', row.water === null ? '' : String(row.water));
    tr.setAttribute('data-sort-air-vehicle', row.air_vehicle === null ? '' : String(row.air_vehicle));

    const identityTd = document.createElement('td');
    const playerWrap = document.createElement('div');
    playerWrap.className = 'mc-board-player';
    playerWrap.appendChild(buildMcAvatarTile(row.uuid));
    playerWrap.appendChild(buildMcNameWrap(row, serverKey));
    identityTd.appendChild(playerWrap);
    tr.appendChild(identityTd);

    // Phase 273 D-14/AFK-03 (plan 273-10): Active Playtime is built FIRST so the Active `<td>`
    // matches detail.latte's column order exactly. It is the ONE cell that threads the row's
    // server-scoped absence reason through fillMcValueCell()'s optional fifth argument — every
    // other cell below keeps its existing three-argument call, unchanged.
    const activeTd = document.createElement('td');
    activeTd.className = 'mc-board-value';
    activeTd.setAttribute('data-metric', 'active_playtime');
    fillMcValueCell(activeTd, row.active_playtime, 'duration', false, row.active_playtime_reason);
    tr.appendChild(activeTd);

    const playtimeTd = document.createElement('td');
    playtimeTd.className = 'mc-board-value';
    playtimeTd.setAttribute('data-metric', 'playtime');
    fillMcValueCell(playtimeTd, row.playtime, 'duration');
    tr.appendChild(playtimeTd);

    const valueCells: Array<[number | null, string]> = [
        [row.blocks_mined, 'count'],
        [row.ground, 'distance_cm'],
        [row.water, 'distance_cm'],
        [row.air_vehicle, 'distance_cm'],
    ];
    for (const [value, unit] of valueCells) {
        const td = document.createElement('td');
        td.className = 'mc-board-value';
        fillMcValueCell(td, value, unit);
        tr.appendChild(td);
    }

    return tr;
}

/**
 * Reconciles the Player Roster table against a fresh poll payload. Rebuilds every row from
 * scratch each tick (the fleet's own lifetime player counts run 6-30/server, per the UI-SPEC's
 * own no-pagination discretion item — cheap to fully rebuild every 30s) rather than diffing by
 * uuid, then re-applies whatever sort the table is CURRENTLY showing — never the server's default
 * order — so an operator's click-to-sort choice survives every live update. Handles both empty-
 * state transitions: a roster that drops to zero rows (should not happen in practice — D-08 never
 * drops a player who has ever played — but handled for symmetry with Today) collapses back to the
 * locked empty-state paragraph, and a roster that gains its first row builds the table shell fresh.
 */
export function updateRosterRows(container: HTMLElement, rows: readonly RosterRow[], serverKey: string): void {
    const section = container.querySelector<HTMLElement>('.mc-roster-section');
    if (!section) return;

    if (rows.length === 0) {
        section.querySelector('.table-responsive')?.remove();
        if (!section.querySelector('p.text-muted')) {
            const p = document.createElement('p');
            p.className = 'text-muted';
            p.textContent = 'No players have played this server yet.';
            section.appendChild(p);
        }
        return;
    }

    let table = section.querySelector<HTMLTableElement>('.mc-roster-table');
    if (!table) {
        section.querySelector('p.text-muted')?.remove();
        const wrap = document.createElement('div');
        wrap.className = 'table-responsive';
        table = buildRosterTableShell();
        wrap.appendChild(table);
        section.appendChild(wrap);
        initRoster(container);
    }

    const controller = controllers.get(table);
    if (!controller) return; // defensive only — initRoster() above always populates this for a table it just built or already knew about

    controller.tbody.replaceChildren();
    for (const row of rows) {
        controller.tbody.appendChild(buildRosterRow(row, serverKey));
    }

    controller.applySort(controller.getState());
}
